// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Divider } from '@material-ui/core';
import { parseISO, getMonth, getYear } from 'date-fns';
import { Flex, Box, useSystem } from 'react-system';
import * as icons from '../../icons';
import { minBy } from '../../utils/array';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../theme';
import { LotPrice, LotPricePerMeterSquared } from '../lot-price';
import type { DevelopmentHeader_development$key } from './__generated__/DevelopmentHeader_development.graphql';

type Props = {|
  development: DevelopmentHeader_development$key,
|};

const getSeason = (month: number, t) => {
  let season = '';
  switch (month) {
    case 11:
    case 0:
    case 1:
      season = t('winter');
      break;
    case 2:
    case 3:
    case 4:
      season = t('spring');
      break;
    case 5:
    case 6:
    case 7:
      season = t('summer');
      break;
    case 8:
    case 9:
    case 10:
      season = t('fall');
      break;
  }
  return season;
};

const getRoomsRange = products => {
  let maximumRooms = 0,
    minimumRooms = 1000;
  products.map(pro => {
    const rooms = pro.property.numberOfRooms;
    if (rooms != null) {
      if (rooms > maximumRooms) {
        maximumRooms = rooms;
      }
      if (rooms < minimumRooms) {
        minimumRooms = rooms;
      }
    }
  });
  return { maximumRooms, minimumRooms };
};

export const DevelopmentHeader = (props: Props): React.Node => {
  const development = useFragment(
    graphql`
      fragment DevelopmentHeader_development on DevelopmentProduct {
        title
        deliveryDate
        products {
          ...lotPrice_product
          ...lotPricePerMeterSquared_product
          salePrice
          property {
            numberOfRooms
            locality
            postcode
          }
        }
      }
    `,
    props.development,
  );
  const { text } = useTheme();
  const { ml, media } = useSystem();
  const { t } = useLocale();

  const iconStyle = [
    ml(['-2px', '-5px']),
    media({ width: [28, 32, 40], height: [28, 32, 40] }),
  ];

  const { products } = development;
  const deliveryDate =
    development.deliveryDate != null
      ? parseISO(development.deliveryDate)
      : null;
  const { maximumRooms, minimumRooms } = getRoomsRange(products);
  const property = products.length === 0 ? null : products[0].property;

  const cheapestProduct = minBy(
    products,
    item => item.salePrice ?? Number.POSITIVE_INFINITY,
  );

  const keyInformation = [
    {
      label: t('lots'),
      value: (
        <Flex>
          <icons.House css={iconStyle} />
          <Box pl={[2, 3]}>{products.length}</Box>
        </Flex>
      ),
      prop: products,
    },

    products.length !== 0
      ? {
          label: t('rooms'),
          value: (
            <Flex>
              <icons.FloorPlan css={iconStyle} />
              <Box pl={[2, 3]}>
                {minimumRooms !== maximumRooms
                  ? `${minimumRooms} - ${maximumRooms}`
                  : minimumRooms}
              </Box>
            </Flex>
          ),
          prop: minimumRooms,
        }
      : null,

    {
      label: t('developmentCompletion'),
      value: (
        <Flex>
          <icons.Date css={iconStyle} />
          {deliveryDate != null && (
            <Box pl={[2, 3]}>
              {`${getSeason(getMonth(deliveryDate), t)} ${getYear(
                deliveryDate,
              )}`}
            </Box>
          )}
        </Flex>
      ),
      prop: deliveryDate,
    },
  ];

  return (
    <>
      <Flex mt={[2, 4]} mb={3} alignItems="left" flexDirection="column">
        <Box
          css={[
            media({ display: ['block', 'none'] }),
            media([text.body2, text.body1]),
          ]}
        >
          {[property?.postcode, ' ', property?.locality]}
        </Box>
        <Box py={[0, 1]} css={media([text.h6, text.h4, text.h2])}>
          {development.title}
        </Box>
        <Box
          css={[
            media({ display: ['none', 'block'] }),
            media([text.body2, text.body1]),
          ]}
        >
          {[property?.postcode, ' ', property?.locality]}
        </Box>
      </Flex>
      <Flex flexWrap="wrap" my={[2, 2, 4]}>
        <Box
          width={1}
          mb={[3, 3, 0]}
          css={media({ display: ['unset', 'none'] })}
        >
          <Box css={media([text.body2, text.body1])} mt={['2px', 1]}>
            {t('pricesStartAt')}
          </Box>
          {cheapestProduct && (
            <Box css={media([text.h6, text.h4])}>
              <LotPrice product={cheapestProduct} />
            </Box>
          )}
          {cheapestProduct && (
            <Box css={media([text.body2, text.body1])} mt={['2px', 1]}>
              <LotPricePerMeterSquared product={cheapestProduct} />
            </Box>
          )}
          <Box pt={3}>
            <Divider />
          </Box>
        </Box>
        <Box flexGrow={1} css={{ minWidth: '240px' }}>
          <Flex flexWrap="wrap" mx={-2}>
            {keyInformation.filter(Boolean).map(
              (info, i) =>
                info.prop != null && (
                  <Box key={i} flexGrow={1} mb={[3, 3, 0]} px={2}>
                    <Box css={media([text.h6, text.h5, text.h4])}>
                      {info.value}
                    </Box>
                    <Box css={media([text.body2, text.body1])} mt={['2px', 1]}>
                      {info.label}
                    </Box>
                  </Box>
                ),
            )}
          </Flex>
        </Box>
      </Flex>
    </>
  );
};
